import { useState, type FormEvent } from 'react';
import { getProactiveSettings, updateProactiveSettings } from './api.js';
import { percentToRate, rateToPercent } from './settings.js';
import { errorText } from './toast.js';
import { useLoad } from './useLoad.js';

/**
 * The tax set-aside rate (an estimate: the share of income the quarterly-tax
 * obligation suggests putting aside). It is stored with the proactive
 * settings but edited here, with the money it applies to. Sends only
 * `taxSetAsideRate`, as a fraction.
 */
export default function TaxSetAside(): JSX.Element | null {
  const settings = useLoad(getProactiveSettings, []);
  if (!settings.data) return null;
  return <TaxForm key={settings.data.taxSetAsideRate} rate={settings.data.taxSetAsideRate} onSaved={() => void settings.reload()} />;
}

function TaxForm({ rate, onSaved }: { rate: number; onSaved: () => void }): JSX.Element {
  const [value, setValue] = useState(rateToPercent(rate));
  const [note, setNote] = useState<{ text: string; error: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(e: FormEvent): Promise<void> {
    e.preventDefault();
    const r = percentToRate(value);
    if (r === null) return setNote({ text: 'Use a percentage from 0 to 100.', error: true });
    if (r === rate) return setNote({ text: 'Nothing changed.', error: false });
    setBusy(true);
    setNote(null);
    try {
      await updateProactiveSettings({ taxSetAsideRate: r });
      setNote({ text: 'Saved.', error: false });
      onSaved();
    } catch (err) {
      setNote({ text: errorText(err), error: true });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="lo-taxrate" onSubmit={save} aria-label="Tax set-aside">
      <label className="lo-taxrate__field">
        Tax set-aside, % of income (estimate)
        <input className="settings-input lo-taxrate__input" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} />
      </label>
      <button type="submit" className="btn btn--ghost btn--sm" disabled={busy}>
        {busy ? 'Saving…' : 'Save'}
      </button>
      {note && (
        <span className={`lo-taxrate__note${note.error ? ' lo-form__error' : ''}`} role={note.error ? 'alert' : 'status'}>
          {note.text}
        </span>
      )}
    </form>
  );
}
